import { useQuery } from '@tanstack/react-query';
import { filtersToSearchParams } from '~/data-grid/utils/filters';
import { useFetch } from '~/hooks/useFetch';
import type { Instant } from '~/utils/temporal';
import { routes } from './routes';
import {
  type JobOperationDetailQueryParams,
  type JobOperationRequestFilters,
  jobOperationDetailQueryParams,
  jobOperationDetailResponseSchema,
  jobOperationFilterValuesResponseSchema,
  jobOperationPollResponseSchema,
  jobOperationRequestQueryParamsSchema,
  jobOperationResponseSchema,
} from './schemas/job-operations.schemas';

const jobOperationsQueryKeys = {
  pollJobOperations: () => [...jobOperationsQueryKeys.root, 'pollJobOperations'],
  getJobOperationDetail: (id: string, params?: JobOperationDetailQueryParams) => [
    ...jobOperationsQueryKeys.root,
    'getJobOperationDetail',
    id,
    params,
  ],
  getJobOperations: (filters: JobOperationRequestFilters) => [
    ...jobOperationsQueryKeys.root,
    'getJobOperations',
    filters,
  ],
  getJobOperationFilterValues: () => [...jobOperationsQueryKeys.root, 'getJobOperationFilterValues'],
  root: ['jobOperations'],
} as const;

export const usePollJobOperations = (
  after: Instant | null,
  options: {
    enabled: boolean;
    refetchInterval: number | false | undefined;
    limit?: number;
  },
) => {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: options.enabled,
    queryFn: () => {
      const params = new URLSearchParams();
      if (after) {
        params.set('after', after.toString());
      }

      if (options.limit) {
        params.set('limit', String(options.limit));
      }

      return fetchData(
        routes.jobOperations.pollJobOperations,
        { response: jobOperationPollResponseSchema },
        {
          queryParams: params.size ? params : undefined,
        },
      );
    },
    queryKey: jobOperationsQueryKeys.pollJobOperations(),
    refetchInterval: options.refetchInterval,
    meta: { disableGlobalErrorHandler: true },
  });
};

export const useGetJobOperations = (filters: JobOperationRequestFilters, options: { enabled: boolean }) => {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: options.enabled,
    queryFn: () => {
      const params = filtersToSearchParams(filters, jobOperationRequestQueryParamsSchema);

      return fetchData(
        routes.jobOperations.getJobOperations,
        { response: jobOperationResponseSchema },
        {
          queryParams: params.size ? params : undefined,
        },
      );
    },
    queryKey: jobOperationsQueryKeys.getJobOperations(filters),
  });
};

export const useGetJobOperationDetail = (
  id: string,
  options: { enabled: boolean },
  queryParams?: JobOperationDetailQueryParams,
) => {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: options.enabled,
    queryFn: () => {
      const params = queryParams ? filtersToSearchParams(queryParams, jobOperationDetailQueryParams) : undefined;

      return fetchData(
        routes.jobOperations.getJobOperationDetail(id),
        { response: jobOperationDetailResponseSchema },
        {
          queryParams: params,
        },
      );
    },
    queryKey: jobOperationsQueryKeys.getJobOperationDetail(id, queryParams),
  });
};

export const useGetJobOperationFilterValues = (options: { enabled: boolean } = { enabled: true }) => {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: options?.enabled,
    queryFn: () => {
      return fetchData(routes.jobOperations.getJobOperationFilterValues, {
        response: jobOperationFilterValuesResponseSchema,
      });
    },
    queryKey: jobOperationsQueryKeys.getJobOperationFilterValues(),
  });
};
